import { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Bell, Warning, XCircle, Checks } from "@phosphor-icons/react";
import { playChime } from "../lib/push";

const timeAgo = (iso) => {
  const m = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return h < 24 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
};

export const AlertsBell = () => {
  const [open, setOpen] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = () => api.get("/alerts").then((r) => {
    setAlerts(r.data.alerts);
    setUnread((prev) => {
      if (r.data.unread > prev) playChime();
      return r.data.unread;
    });
  }).catch(() => {});

  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", close);
    return () => { clearInterval(t); document.removeEventListener("mousedown", close); };
  }, []);

  const markAll = async () => { await api.post("/alerts/read-all"); load(); };

  return (
    <div className="relative" ref={ref}>
      <button data-testid="alerts-bell-button" onClick={() => setOpen(!open)}
        className="relative h-11 w-11 rounded-full bg-white border border-slate-200 flex items-center justify-center tap-press hover:border-slate-900">
        <Bell size={22} weight="duotone" className="text-slate-700" />
        {unread > 0 && (
          <span data-testid="alerts-unread-badge"
            className="absolute -top-1 -right-1 h-5 min-w-5 px-1 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center">
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div data-testid="alerts-panel"
          className="absolute right-0 mt-2 w-96 max-w-[90vw] bg-white border border-slate-200 rounded-2xl shadow-xl z-50 rise-in overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <p className="font-heading font-bold">Escalation Alerts</p>
            {unread > 0 && (
              <button data-testid="alerts-mark-all-read" onClick={markAll}
                className="text-xs font-bold text-slate-500 hover:text-slate-900 flex items-center gap-1 tap-press">
                <Checks size={16} /> Mark all read
              </button>
            )}
          </div>
          <div className="max-h-96 overflow-y-auto">
            {alerts.length === 0 && <p className="p-6 text-sm text-slate-400 text-center">No alerts — smooth service.</p>}
            {alerts.map((a) => (
              <div key={a.id} data-testid={`alert-item-${a.id}`}
                className={`px-5 py-4 border-b border-slate-50 border-l-4 ${
                  a.type === "task_overdue" ? "border-l-red-500" : "border-l-amber-400"} ${!a.read ? "bg-slate-50" : ""}`}>
                <div className="flex gap-3">
                  {a.type === "task_overdue"
                    ? <Warning size={20} weight="duotone" className="text-red-500 shrink-0 mt-0.5" />
                    : <XCircle size={20} weight="duotone" className="text-amber-500 shrink-0 mt-0.5" />}
                  <div>
                    <p className="text-sm font-semibold leading-snug">{a.message}</p>
                    <p className="text-xs text-slate-400 mt-1">{timeAgo(a.created_at)} · {a.type === "task_overdue" ? "Task escalation" : "Readiness escalation"}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="px-5 py-3 text-[11px] text-slate-400 bg-slate-50 border-t border-slate-100">
            Email / WhatsApp escalation channels can be connected in a later update.
          </p>
        </div>
      )}
    </div>
  );
};
